/**
 * Slice 53 — Walk and After by room. Photos are kept per room (and also as the flat list the rest
 * of the app already reads), so the owner can see exactly which room each photo is of.
 */
import type { RoomShots, SimpleFlowState } from "@/lib/simpleTurnover";
import type { RoomShot } from "@/lib/photoGroups";

export type RoomPhase = "before" | "after";

/**
 * A room needs at least one photo before it can be marked done. There is no photo target shown to the
 * cleaner: they are told to photograph everything in the room, however many photos that takes.
 */
export const MIN_ROOM_PHOTOS = 1;

export interface WalkRoom {
  id: string;
  name: string;
  coverUrl: string | null;
  /** The room's reference photos for the cleaner to swipe: cover first, then the listing photos. */
  refUrls: string[];
  /** Owner chose "No photo for this room": show cleaners no reference at all. */
  noCover: boolean;
  /** This room's unit items (durable things like lamp, TV, mirror). Info only. */
  items: {
    id: string;
    name: string;
    photoUrl: string | null;
    quantity: number | null;
    groupId: string | null;
    /** Unit items link to flags; supplies and appliances live in spots too. */
    kind: "unit" | "supply" | "appliance";
  }[];
  /** Slice 57 — the guided Walk shot list: whole room first, then one per photo group. */
  shots: RoomShot[];
}

export type RoomStatus = "done" | "skipped" | "open";

export function readByRoom(state: SimpleFlowState, phase: RoomPhase): Record<string, RoomShots> {
  return (phase === "before" ? state.walk_by_room : state.after_by_room) ?? {};
}

export function roomStatus(shots: RoomShots | undefined): RoomStatus {
  if (shots?.skip) return "skipped";
  if (shots?.done) return "done";
  return "open";
}

/** First room that is neither done nor skipped; -1 when every room is handled. */
export function firstOpenRoomIndex(rooms: WalkRoom[], by: Record<string, RoomShots>): number {
  return rooms.findIndex((r) => roomStatus(by[r.id]) === "open");
}

/** All rooms' photos in walk order: the flat list the rest of the app reads. */
export function flattenRoomPhotos(rooms: WalkRoom[], by: Record<string, RoomShots>): string[] {
  const out: string[] = [];
  for (const room of rooms) {
    for (const url of by[room.id]?.photo_urls ?? []) {
      if (url && !url.startsWith("blob:")) out.push(url);
    }
  }
  return out;
}

/** Bedding is only asked for in rooms where there is a bed. */
export function roomHasBedding(roomName: string): boolean {
  return /bed|dorm|habitaci|suite|master/i.test(roomName);
}

export function photosStillNeeded(count: number): number {
  return Math.max(0, MIN_ROOM_PHOTOS - count);
}
